import React, { useState } from 'react';
import SignUpForm from '../components/SignUpForm';
import { submitPublicContact } from '../services/contactService';
import { useFlag, FLAGS } from '../FlagProvider';

export default function Homepage() {
  // feature.public_signup already 404s POST /api/public/signup server-side
  // (cmd/api/signup.go:127, the one production requireFlag call site). Without
  // this, the button and modal below would still render a working-looking
  // form that dead-ends in a 404 -- exactly the staging scenario the flag
  // exists for. This is the only entry point into SignUpForm on the site.
  const publicSignupEnabled = useFlag(FLAGS.publicSignup);
  // feature.public_contact already 404s POST /api/public/contact server-side
  // (cmd/api/contact.go's contactRoutes). Without this, the two buttons below
  // and the modal they open would still render a working-looking form that
  // dead-ends in that 404 -- the same staging scenario the signup flag above
  // exists for. These two buttons are the only entry point into the contact
  // modal on the site.
  const publicContactEnabled = useFlag(FLAGS.publicContact);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactType, setContactType] = useState('early_access');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);

  const primaryColor = '#00C6AE';
  const primaryDark = '#FFFFFF';
  const primaryDarkAlt = '#F9FAFB';
  const textPrimary = '#111827';
  const textSecondary = '#6B7280';
  const textMuted = '#9CA3AF';
  const borderColor = '#E5E7EB';
  const cardBg = '#FFFFFF';

  const styles = {
    body: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: primaryDark,
      color: textPrimary,
      lineHeight: 1.6,
      WebkitFontSmoothing: 'antialiased',
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 24px',
    },
    navbar: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${borderColor}`,
      zIndex: 1000,
      padding: '16px 0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    navContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      textDecoration: 'none',
      color: textPrimary,
      fontWeight: 700,
      fontSize: '20px',
    },
    logoImg: {
      height: '32px',
      width: 'auto',
    },
    navMenu: {
      display: 'flex',
      listStyle: 'none',
      gap: '32px',
      alignItems: 'center',
      margin: 0,
      padding: 0,
    },
    navLink: {
      color: textSecondary,
      textDecoration: 'none',
      fontWeight: 500,
      fontSize: '15px',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    btn: {
      display: 'inline-block',
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '15px',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      border: 'none',
      cursor: 'pointer',
      textAlign: 'center',
    },
    btnPrimary: {
      background: `linear-gradient(135deg, ${primaryColor}, #00B8D9)`,
      color: primaryDark,
    },
    btnSecondary: {
      background: 'transparent',
      color: primaryColor,
      border: `2px solid ${primaryColor}`,
    },
    btnLarge: {
      padding: '16px 32px',
      fontSize: '16px',
    },
    hero: {
      padding: '160px 0 100px',
      background: `linear-gradient(180deg, ${primaryDark} 0%, ${primaryDarkAlt} 100%)`,
      position: 'relative',
      overflow: 'hidden',
    },
    heroTitle: {
      fontSize: '72px',
      fontWeight: 700,
      lineHeight: 1.1,
      marginBottom: '24px',
      background: `linear-gradient(135deg, ${textPrimary} 0%, ${primaryColor} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    heroSubtitle: {
      fontSize: '20px',
      color: textSecondary,
      marginBottom: '40px',
      lineHeight: 1.6,
    },
    heroCta: {
      display: 'flex',
      gap: '16px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    },
    heroNote: {
      color: textMuted,
      fontSize: '14px',
    },
    section: {
      padding: '100px 0',
    },
    sectionTitle: {
      fontSize: '42px',
      fontWeight: 700,
      textAlign: 'center',
      marginBottom: '16px',
    },
    problemSection: {
      background: primaryDarkAlt,
      borderTop: `1px solid ${borderColor}`,
      borderBottom: `1px solid ${borderColor}`,
    },
    problemGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '32px',
      marginTop: '60px',
    },
    problemCard: {
      background: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    problemIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '64px',
      height: '64px',
      margin: '0 auto 16px',
      borderRadius: '12px',
      background: `linear-gradient(135deg, ${primaryColor}, #00B8D9)`,
      padding: '12px',
    },
    problemCardH3: {
      fontSize: '20px',
      marginBottom: '12px',
      color: primaryColor,
    },
    problemCardP: {
      color: textSecondary,
    },
    benefitsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '32px',
      marginTop: '60px',
    },
    benefitCard: {
      background: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '40px',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    benefitNumber: {
      fontSize: '42px',
      fontWeight: 700,
      background: `linear-gradient(135deg, ${primaryColor}, #00B8D9)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '16px',
      whiteSpace: 'nowrap',
    },
    steps: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      marginTop: '60px',
    },
    step: {
      textAlign: 'center',
    },
    stepNumber: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${primaryColor}, #00B8D9)`,
      color: primaryDark,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      fontWeight: 700,
      margin: '0 auto 24px',
    },
    stepH3: {
      fontSize: '24px',
      marginBottom: '12px',
      color: primaryColor,
    },
    ctaSection: {
      background: `linear-gradient(135deg, ${primaryDarkAlt} 0%, ${primaryDark} 100%)`,
      textAlign: 'center',
      padding: '100px 0',
      borderTop: `1px solid ${borderColor}`,
    },
    footer: {
      background: primaryDarkAlt,
      borderTop: `1px solid ${borderColor}`,
      padding: '60px 0 32px',
      boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.1)',
    },
    footerContent: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '40px',
      marginBottom: '40px',
    },
    footerLogo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px',
    },
    footerLogoText: {
      fontWeight: 700,
      fontSize: '20px',
      background: `linear-gradient(135deg, ${primaryColor}, #00B8D9)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    heroGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '40px',
      alignItems: 'center',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      borderRadius: '999px',
      background: '#E5F9F6',
      color: primaryColor,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: '16px',
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      padding: '16px',
    },
    modal: {
      width: '100%',
      maxWidth: '480px',
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)',
    },
    modalTitle: {
      fontSize: '22px',
      fontWeight: 700,
      marginBottom: '4px',
      color: textPrimary,
    },
    modalSubtitle: {
      fontSize: '14px',
      color: textSecondary,
      marginBottom: '20px',
    },
    modalLabel: {
      display: 'block',
      fontSize: '13px',
      fontWeight: 500,
      color: textPrimary,
      marginBottom: '6px',
    },
    modalInput: {
      width: '100%',
      borderRadius: '8px',
      border: `1px solid ${borderColor}`,
      padding: '10px 12px',
      fontSize: '14px',
      marginBottom: '12px',
      outline: 'none',
    },
    modalTextarea: {
      width: '100%',
      borderRadius: '8px',
      border: `1px solid ${borderColor}`,
      padding: '10px 12px',
      fontSize: '14px',
      minHeight: '120px',
      resize: 'vertical',
      marginBottom: '12px',
      outline: 'none',
    },
    modalHelper: {
      fontSize: '12px',
      color: textMuted,
      marginBottom: '12px',
    },
    modalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '8px',
      gap: '12px',
      flexWrap: 'wrap',
    },
    modalError: {
      fontSize: '13px',
      color: '#B91C1C',
    },
    modalSuccess: {
      fontSize: '13px',
      color: primaryColor,
    },
  };

  const openContact = (type) => {
    setContactType(type);
    setContactForm({
      name: '',
      email: '',
      company: '',
      message: '',
    });
    setContactError('');
    setContactSuccess(false);
    setShowContact(true);
  };

  const handleContactChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (contactSubmitting) return;

    setContactError('');
    setContactSuccess(false);

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactError('Please provide your name, email, and a short message.');
      return;
    }

    try {
      setContactSubmitting(true);
      await submitPublicContact({
        type: contactType,
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        message: contactForm.message,
      });
      setContactSuccess(true);
      setContactForm((prev) => ({ ...prev, message: '' }));
    } catch (err) {
      setContactError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div style={styles.body}>
      {/* Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.container}>
          <div style={styles.navContent}>
            <a href="/" style={styles.logo}>
              <img src="/sc_white.png" alt="SignalCue Logo" style={styles.logoImg} />
            </a>
            <ul style={styles.navMenu}>
              <li><button onClick={() => {}} style={{ ...styles.navLink, background: 'none', border: 'none', padding: 0 }}>Product</button></li>
              <li><button onClick={() => {}} style={{ ...styles.navLink, background: 'none', border: 'none', padding: 0 }}>Solutions</button></li>
              <li><button onClick={() => {}} style={{ ...styles.navLink, background: 'none', border: 'none', padding: 0 }}>Pricing</button></li>
              <li><a href="/login" style={styles.navLink}>Login</a></li>
              {publicSignupEnabled && (
                <li>
                  <button
                    onClick={() => setShowSignUp(true)}
                    style={{ ...styles.btn, ...styles.btnPrimary }}
                  >
                    Sign Up
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>

      {/* Sign Up Modal */}
      {showSignUp && publicSignupEnabled && (
        <SignUpForm onClose={() => setShowSignUp(false)} />
      )}

      {/* Contact Modal */}
      {showContact && publicContactEnabled && (
        <div style={styles.modalOverlay} onClick={() => setShowContact(false)}>
          <div
            style={styles.modal}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2 style={styles.modalTitle}>
              {contactType === 'founder' ? 'Speak to the founder' : 'Request early access'}
            </h2>
            <p style={styles.modalSubtitle}>
              Share a little context and we&apos;ll reply personally via email.
            </p>
            <form onSubmit={handleSubmitContact}>
              <label style={styles.modalLabel} htmlFor="contact-name">
                Name
              </label>
              <input
                id="contact-name"
                type="text"
                style={styles.modalInput}
                value={contactForm.name}
                onChange={(e) => handleContactChange('name', e.target.value)}
                placeholder="Your name"
              />

              <label style={styles.modalLabel} htmlFor="contact-email">
                Work email
              </label>
              <input
                id="contact-email"
                type="email"
                style={styles.modalInput}
                value={contactForm.email}
                onChange={(e) => handleContactChange('email', e.target.value)}
                placeholder="you@agency.com"
              />

              <label style={styles.modalLabel} htmlFor="contact-company">
                Agency
              </label>
              <input
                id="contact-company"
                type="text"
                style={styles.modalInput}
                value={contactForm.company}
                onChange={(e) => handleContactChange('company', e.target.value)}
                placeholder="Your recruitment agency (optional)"
              />

              <label style={styles.modalLabel} htmlFor="contact-message">
                Message
              </label>
              <textarea
                id="contact-message"
                style={styles.modalTextarea}
                value={contactForm.message}
                onChange={(e) => handleContactChange('message', e.target.value)}
                placeholder={
                  contactType === 'founder'
                    ? 'Share a bit about your role, team, and what you\'d like to discuss.'
                    : 'Tell us briefly about your agency and why early access might be useful.'
                }
              />

              <p style={styles.modalHelper}>
                Messages are sent securely to <strong>admin@signalcue.ai</strong>. We typically reply within one business day.
              </p>

              <div style={styles.modalFooter}>
                <div>
                  {contactError && <div style={styles.modalError}>{contactError}</div>}
                  {contactSuccess && !contactError && (
                    <div style={styles.modalSuccess}>Thank you – your message has been sent.</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setShowContact(false)}
                    style={{ ...styles.btn, ...styles.btnSecondary }}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    style={{
                      ...styles.btn,
                      ...styles.btnPrimary,
                      opacity: contactSubmitting ? 0.7 : 1,
                    }}
                  >
                    {contactSubmitting ? 'Sending…' : 'Send message'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.container}>
          <div style={styles.heroGrid}>
            <div>
              <div style={styles.badge}>
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '999px',
                    backgroundColor: primaryColor,
                  }}
                />
                Private beta
              </div>
              <h1 style={styles.heroTitle}>
                SignalCue is building the intelligence layer for recruitment agencies.
              </h1>
              <p style={styles.heroSubtitle}>
                We&apos;re quietly working with a small group of forward-thinking recruitment leaders to shape what comes next.
                If you&apos;d like to stay close to the journey, we&apos;d love to hear from you.
              </p>
              {/* The note is a caption for these two buttons -- it promises what
                  happens when you press one. With feature.public_contact off it
                  has to go with them, or the hero ends on "Just a short
                  conversation" with nothing to start one. */}
              {publicContactEnabled && (
                <>
                  <div style={styles.heroCta}>
                    <button
                      onClick={() => openContact('early_access')}
                      style={{ ...styles.btn, ...styles.btnPrimary, ...styles.btnLarge }}
                    >
                      Request early access
                    </button>
                    <button
                      onClick={() => openContact('founder')}
                      style={{ ...styles.btn, ...styles.btnSecondary, ...styles.btnLarge }}
                    >
                      Speak to the founder
                    </button>
                  </div>
                  <p style={styles.heroNote}>
                    No pitch decks. No feature tours. Just a short conversation about where recruitment intelligence is heading.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Stealth Mode Section */}
      <section style={{ ...styles.section, ...styles.problemSection }}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>A focused, invite-only beta</h2>
          <p
            style={{
              fontSize: '18px',
              color: textSecondary,
              textAlign: 'center',
              maxWidth: '720px',
              margin: '0 auto',
            }}
          >
            During this phase we&apos;re partnering with a small number of recruitment agencies to co-design the product,
            pressure-test ideas in real teams, and make sure we&apos;re solving meaningful problems before talking more
            broadly.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.container}>
          <div style={styles.footerContent}>
            <div>
              <div style={styles.footerLogo}>
                <img src="/sc_white.png" alt="SignalCue" style={{ height: '32px', width: 'auto' }} />
              </div>
              <p style={{ color: textMuted, fontSize: '14px' }}>Recruitment Intelligence Platform</p>
            </div>
            <div>
              <h4 style={{ fontSize: '16px', marginBottom: '16px', color: primaryColor }}>Product</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>How It Works</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>Features</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>Integrations</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>Pricing</span></li>
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '16px', marginBottom: '16px', color: primaryColor }}>Solutions</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>For Recruiters</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>For Managers</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>For Operations</span></li>
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '16px', marginBottom: '16px', color: primaryColor }}>Company</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>Success Stories</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>FAQ</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>Security & Compliance</span></li>
                <li style={{ marginBottom: '12px' }}><span style={{ color: textSecondary, fontSize: '14px' }}>Contact</span></li>
              </ul>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '32px', borderTop: `1px solid ${borderColor}`, flexWrap: 'wrap', gap: '16px' }}>
            <p style={{ color: textMuted, fontSize: '14px' }}>&copy; 2026 SignalCue. All rights reserved.</p>
            <div style={{ display: 'flex', gap: '24px' }}>
              <span style={{ color: textMuted, fontSize: '14px' }}>Privacy Policy</span>
              <span style={{ color: textMuted, fontSize: '14px' }}>Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}